import { createStore, applyMiddleware, compose } from "redux";
import thunk from "redux-thunk";
import { combineReducers } from "redux";
import { deepCopy, RehydrateThumbnails } from "../components/Common";
import { getCookie } from "../components/Common";
import appState, { initialState as appStateinitialState, TOGGLE_DESIGNER } from "./appState";
import jobState, { initialState as jobStateinitialState } from "./jobState";
import {
    UPDATE_2D_DESIGNER_SVGS,
    UPDATE_3D_MODEL,
    UPDATE_SITE_PARAMETERS,
    UPDATE_AREA_SPECIFICATION,
    UPDATE_CUSTOMER,
} from "./jobState";
import configState, { initialState as configStateinitialState } from "./configState";
import _merge from "lodash/merge";
import { decryptData } from "./database";
import { PRICEFILE_KEY } from "../AppConfig";

export const UNDO = "UNDO";
export const REDO = "REDO";

// local storage
export const loadState = () => {
    try {
        const serializedState = localStorage.getItem("state");
        if (serializedState === null) {
            return undefined;
        }
        return JSON.parse(serializedState);
    } catch (err) {
        return undefined;
    }
};

export const saveState = (state) => {
    try {
        const serializedState = JSON.stringify({
            app: { ...state.app, pricefile: null },
            config: { ...state.config },
        });
        localStorage.setItem("state", serializedState);
    } catch (error) {}
};

const appReducer = combineReducers({
    app: appState,
    job: jobState,
    config: configState,
});

export const rootReducer = (state, action) => {
    if (action.type === "LOG_OUT") {
        saveState({});
    }

    if (action.type === "RESET_ALL") {
        state = mergedState({});
    }

    if (action.type === "SET_STATE") {
        state = action.value;
    }

    if (action.type === "PRESISTED_STATE") {
        let old_state = { ...state };
        state = {
            ...mergedState({}),
            ...persistedState,
            app: {
                ...persistedState.app,
                user: { ...old_state.app.user },
                designer: appStateinitialState.designer,
            },
            job: {
                ...old_state.job,
                ...persistedState.job,
            },
        };
    }

    if (action.type === "LOAD_TEMPLATE") {
        state = mergedState(action.value);
    }

    if (action.type === UNDO || action.type === REDO) {
        state = { ...state };
        let pointer = state.app.job_history_buffer.pointer + (action.type === REDO ? 1 : -1);

        if (pointer < -1) pointer = 0;
        if (pointer > state.app.job_history_buffer.data.length - 1)
            pointer = state.app.job_history_buffer.data.length - 1;

        let recall_state = deepCopy(state.app.job_history_buffer.data[pointer]);

        if (recall_state.threeDMode !== undefined) {
            state.app.designer = recall_state;
        } else {
            state.job = recall_state;
            if (state.job.UNDO_ACTION) {
                if (
                    state.job.UNDO_ACTION.type === UPDATE_AREA_SPECIFICATION &&
                    state.job.UNDO_ACTION.section === "SKYLIGHTS"
                ) {
                    state.job.AreaSpecifications[0].SHAPE.Redraw = Date.now();
                }
            }
        }

        state.app.job_history_buffer.pointer = pointer;
    }

    let newState = appReducer(state, action);

    if (
        action.type === UPDATE_SITE_PARAMETERS ||
        action.type === UPDATE_AREA_SPECIFICATION ||
        action.type === UPDATE_CUSTOMER ||
        action.type === "NEW_JOB" ||
        action.type === "ADD_NEW_AREA" ||
        action.type === "DELETE_AREA"
    ) {
        let job_history_buffer = { ...newState.app.job_history_buffer };

        if (action.type === TOGGLE_DESIGNER && action.parameter === "threeDMode") {
            let designer_state = deepCopy(state.app.designer);
            job_history_buffer.data.push(designer_state);

            if (job_history_buffer.data.length > 10) job_history_buffer.data = job_history_buffer.data.slice(-10);
            job_history_buffer.pointer = job_history_buffer.data.length - 1;
        } else {
            let state_copy = deepCopy(newState.job);

            if (state_copy) {
                state_copy.UNDO_ACTION = { ...action, config: null };

                if (typeof state_copy.UNDO_ACTION.value === "object") state_copy.UNDO_ACTION.value = null;
                if (typeof state_copy.UNDO_ACTION.parameter === "object") state_copy.UNDO_ACTION.parameter = null;
            }

            if (window.last_saved_undo && Date.now() - window.last_saved_undo < 500) {
                job_history_buffer.data[job_history_buffer.pointer] = state_copy;
            } else {
                job_history_buffer.data = job_history_buffer.data.slice(0, job_history_buffer.pointer + 1);

                if (action.type === "NEW_JOB" && job_history_buffer?.data?.[0]?.id !== newState?.job?.id) {
                    job_history_buffer.data = [];
                }
                job_history_buffer.data.push(state_copy);

                if (job_history_buffer.data.length > 10) job_history_buffer.data = job_history_buffer.data.slice(-10);
                job_history_buffer.pointer = job_history_buffer.data.length - 1;
            }
        }
        newState.app.job_history_buffer = job_history_buffer;
        window.last_saved_undo = Date.now();
    } else if (action.type === UPDATE_2D_DESIGNER_SVGS) {
        let job_history_buffer = { ...newState.app.job_history_buffer };
        job_history_buffer.data[job_history_buffer.pointer].App.current_SVG = action.value;
        newState.app.job_history_buffer = job_history_buffer;
    } else if (action.type === UPDATE_3D_MODEL) {
        let job_history_buffer = { ...newState.app.job_history_buffer };
        job_history_buffer.data[job_history_buffer.pointer].App.current_OBJ = action.value;
        newState.app.job_history_buffer = job_history_buffer;
    }

    return newState;
};

const middleware = [thunk];
export const persistedState = loadState();

export async function mergedState(in_initial_state) {
    let initial_state = deepCopy(in_initial_state);

    if (window.debug && window.location.href.includes("static")) {
        return persistedState;
    }

    if (persistedState?.config) {
        initial_state["config"] = { ...persistedState.config };
    }

    if (!initial_state?.app) initial_state.app = {};

    if (persistedState?.app?.user?.id === getCookie("SF_user")) {
        if (persistedState?.app?.offerings) initial_state.app["offerings"] = { ...persistedState.app.offerings };
        if (persistedState?.app?.user_last_jobs_edited && Array.isArray(persistedState?.app?.user_last_jobs_edited))
            initial_state.app["user_last_jobs_edited"] = [...persistedState.app.user_last_jobs_edited];
        if (persistedState?.app?.user) initial_state.app["user"] = { ...persistedState.app.user };
    }

    let encryptedPricefile = null;

    // Decrypt the encrypted data
    let pricefile_data_session;

    if (encryptedPricefile?.data && encryptedPricefile?.iv)
        pricefile_data_session = decryptData(
            encryptedPricefile.data, // Access the "data" property that contains the encrypted data
            encryptedPricefile.iv,
            PRICEFILE_KEY // Encyrption Key
        );

    if (pricefile_data_session && pricefile_data_session.startsWith("{"))
        initial_state.app["pricefile"] = RehydrateThumbnails(JSON.parse(pricefile_data_session));
    else if (persistedState?.app?.pricefile) initial_state.app["pricefile"] = { ...persistedState?.app?.pricefile };

    if (persistedState?.app?.announcements) initial_state.app["announcements"] = [...persistedState.app.announcements];
    initial_state.app["designer"] = {};

    if (persistedState?.app?.designer?.designer2D)
        initial_state.app["designer"]["designer2D"] = { ...persistedState?.app?.designer?.designer2D };
    if (persistedState?.app?.designer?.designer3D)
        initial_state.app["designer"]["designer3D"] = {
            ...persistedState?.app?.designer?.designer3D,
            objs: {
                area_all: null,
            },
        };

    if (persistedState?.app?.dist_levels) initial_state.app["dist_levels"] = { ...persistedState?.app?.dist_levels };
    if (persistedState?.app?.special_factors)
        initial_state.app["special_factors"] = { ...persistedState?.app?.special_factors };

    let merged = initial_state;

    merged = _merge({}, { app: appStateinitialState }, merged);
    merged = _merge({}, { job: jobStateinitialState }, merged);
    merged = _merge({}, { config: configStateinitialState }, merged);

    merged.job = null;

    if (merged?.config) window.config = merged?.config;

    return merged;
}

const isChrome = () => {
    var isChromium = window.chrome;
    var winNav = window.navigator;
    var vendorName = winNav.vendor;
    var isOpera = typeof window.opr !== "undefined";
    var isIOSChrome = winNav.userAgent.match("CriOS");

    if (isIOSChrome) {
        // is Google Chrome on IOS
        return false;
    } else if (
        isChromium !== null &&
        typeof isChromium !== "undefined" &&
        vendorName === "Google Inc." &&
        isOpera === false
    ) {
        // is Google Chrome
        return true;
    } else {
        // not Google Chrome
        return false;
    }
};

const store = createStore(
    rootReducer,
    {},
    (window.location.href.includes("localhost") || window.location.href.includes("test.")) &&
        isChrome() &&
        window?.__REDUX_DEVTOOLS_EXTENSION__
        ? compose(
              applyMiddleware(...middleware),
              window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()
          )
        : compose(applyMiddleware(...middleware))
);

store.subscribe(() => {
    saveState(store.getState());
});

export default store;
